import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { TFunction } from "i18next";
import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  calculateCraveScore,
  CRAVE_BASELINE,
  defaultCraveScoreConfig,
  MetricConfig,
  MetricContribution,
  MetricInput,
} from "@/utils/craveScore";
import { formatDecimal, formatPercentFromWhole } from "@/utils/number";
import { formatDurationShort } from "@/utils/time";

// Respect user motion preferences
const usePrefersReducedMotion = (): boolean => {
  const [prefers, setPrefers] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefers(m.matches);
    // initialize
    onChange();
    // subscribe
    if (typeof m.addEventListener === "function") {
      m.addEventListener("change", onChange);
      return () => m.removeEventListener("change", onChange);
    } else if (typeof m.addListener === "function") {
      m.addListener(onChange);
      return () => m.removeListener(onChange);
    }
  }, []);
  return prefers;
};

interface CraveScoreCardProps {
  inputs: MetricInput[];
  config?: MetricConfig[];
}

const formatPoints = (pts: number): string => {
  const sign = pts > 0 ? "+" : pts < 0 ? "−" : "";
  const abs = Math.abs(pts);
  return `${sign}${abs.toFixed(1)}`;
};

// Blue gradient colors to align with page title
const BLUE_GRADIENT_FROM = "#06F"; // #0066ff
const BLUE_GRADIENT_TO = "#00B7FF";
const BLUE_PRIMARY = "#0066ff";

// Negative (left) bar gradient: yellow near center → red toward the extreme
const NEG_GRADIENT_FROM = "#FF9F0A"; // iOS orange (subtle, near center)
const NEG_GRADIENT_TO = "#FF3B30"; // iOS red (toward left edge)

const scoreToStatus = (score: number, t: TFunction) => {
  if (score >= CRAVE_BASELINE + 15)
    return { label: t("common.craveScoreCard.status.crushingIt"), tone: "success" as const };
  if (score >= 75) return { label: t("common.craveScoreCard.status.onTrack"), tone: "ok" as const };
  return { label: t("common.craveScoreCard.status.needsAttention"), tone: "warn" as const };
};

// Friendly encouragement message for the middle guidance card
const getGenericEncouragement = (score: number, t: TFunction) => {
  if (score >= 90)
    return {
      icon: "✓",
      title: t("common.craveScoreCard.encouragement.generic.phenomenalMomentum.title"),
      text: t("common.craveScoreCard.encouragement.generic.phenomenalMomentum.text"),
    };
  if (score >= 80)
    return {
      icon: "✓",
      title: t("common.craveScoreCard.encouragement.generic.onTrack.title"),
      text: t("common.craveScoreCard.encouragement.generic.onTrack.text"),
    };
  if (score >= 70)
    return {
      icon: "!",
      title: t("common.craveScoreCard.encouragement.generic.soClose.title"),
      text: t("common.craveScoreCard.encouragement.generic.soClose.text"),
    };
  if (score >= 60)
    return {
      icon: "!",
      title: t("common.craveScoreCard.encouragement.generic.makingProgress.title"),
      text: t("common.craveScoreCard.encouragement.generic.makingProgress.text"),
    };
  return {
    icon: "✖",
    title: t("common.craveScoreCard.encouragement.generic.tuneItUp.title"),
    text: t("common.craveScoreCard.encouragement.generic.tuneItUp.text"),
  };
};

const CraveScoreCard: React.FC<CraveScoreCardProps> = ({ inputs, config }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const cfg = config || defaultCraveScoreConfig;
  const gradId = useId();
  // Per-category expanded state for granular details
  const [expanded, setExpanded] = useState<Record<TrioId, boolean>>({
    Service: false,
    Merchandise: false,
    Experience: false,
  });
  const isOpen = (id: TrioId) => !!expanded[id];
  const toggleOpen = (id: TrioId) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  const setAll = (open: boolean) =>
    setExpanded({ Service: open, Merchandise: open, Experience: open });
  const prefersReducedMotion = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Debounced/stable crave score calculation to avoid UI jumping mid-update
  const useStableCraveScore = (
    cfgArg: typeof cfg,
    inputsArg: typeof inputs,
    debounceMs: number = 300,
  ) => {
    const [result, setResult] = useState(() => calculateCraveScore(cfgArg, inputsArg));
    const [isPending, setIsPending] = useState(false);
    const timerRef = React.useRef<number | null>(null);

    useEffect(() => {
      // start debounce window; keep previous UI until settled
      setIsPending(true);
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => {
        const next = calculateCraveScore(cfgArg, inputsArg);
        setResult(next);
        setIsPending(false);
      }, debounceMs);
      return () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
      };
    }, [cfgArg, inputsArg, debounceMs]);

    return { result, isPending } as const;
  };

  const { result, isPending } = useStableCraveScore(cfg, inputs, 300);

  const score = result.score > 0 && result.score < 1 ? result.score : Math.round(result.score);
  const scoreText =
    result.score > 0 && result.score < 1
      ? formatDecimal(result.score, currentLanguage, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
      : score.toString();
  const status = scoreToStatus(score, t);
  const color = BLUE_PRIMARY;
  const isWarn = status.tone === "warn";
  const isWarnVisual = isWarn && score > 0; // keep blue gradients when score is exactly 0
  // encouragement is derived later from insights (see below)

  // Circular gauge geometry
  const size = 220; // px
  const stroke = 16; // px
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(1, score / 100));
  const dashOffset = circumference * (1 - progress);

  // Stable contributions order: follow the metric config order by id, with alpha fallback
  const orderIndex = useMemo(() => {
    const map = new Map<string, number>();
    cfg.forEach((m, idx) => map.set(m.id, idx));
    return map;
  }, [cfg]);

  const sorted: MetricContribution[] = useMemo(() => {
    const arr = [...result.contributions];
    arr.sort((a, b) => {
      const ai = orderIndex.get(a.id);
      const bi = orderIndex.get(b.id);
      if (ai !== undefined && bi !== undefined && ai !== bi) return ai - bi;
      if (ai !== undefined && bi === undefined) return -1;
      if (ai === undefined && bi !== undefined) return 1;
      const al = a.label.toLowerCase();
      const bl = b.label.toLowerCase();
      if (al < bl) return -1;
      if (al > bl) return 1;
      return a.id.localeCompare(b.id);
    });
    return arr;
  }, [result.contributions, orderIndex]);

  // Maximum absolute impact used to scale the diverging bars
  const maxAbs = useMemo(() => {
    const vals = sorted.map((c) => Math.abs(c.points));
    const m = Math.max(0, ...vals);
    return m > 0 ? m : 1; // avoid divide-by-zero
  }, [sorted]);

  // Build a quick lookup for contributions by metric id
  const contribById = useMemo(() => {
    const map = new Map<string, MetricContribution>();
    for (const c of result.contributions) map.set(c.id, c);
    return map;
  }, [result.contributions]);

  // Formatter helpers to ensure values and goals share identical formatting per metric
  const toNum = (v: any): number | null => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const fmtPct = (v: any): string => {
    const n = toNum(v);
    return n == null ? "—" : formatPercentFromWhole(n, currentLanguage);
  };
  const fmtSec = (v: any): string => {
    const n = toNum(v);
    if (n == null) return "—";
    const total = Math.max(0, Math.round(n));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m > 0 ? `${m}m${s}s` : `${s}s`;
  };
  const fmtNum1 = (v: any): string => {
    const n = toNum(v);
    return n == null ? "—" : n.toFixed(1);
  };
  const formatMetricValue = (metricId: string, v: any): string => {
    switch (metricId) {
      case "missedPct":
      case "cantFindPct":
        return fmtPct(v);
      case "avgResponseSec":
        return fmtSec(v);
      case "ups":
      case "satisfaction":
        return fmtNum1(v);
      default:
        return v == null ? "—" : String(v);
    }
  };

  // Simplified trio categories
  type TrioId = "Service" | "Merchandise" | "Experience";
  type TrioItem = {
    id: TrioId;
    label: string;
    points: number;
    details: string;
    sub: { label: string; val: string; goal: string }[];
  };

  const trio: TrioItem[] = useMemo(() => {
    const get = (id: string) => contribById.get(id);

    const missed = get("missedPct");
    const response = get("avgResponseSec");
    const cantFind = get("cantFindPct");
    const ups = get("ups");
    const exp = get("satisfaction");

    const fmtMetricNumber = (
      metricId: string | undefined,
      value: string | number | null | undefined,
    ) => {
      if (value == null) return "—";
      const num = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(num)) return String(value);
      if (metricId === "avgResponseSec" || metricId === "dwellSec") {
        return formatDurationShort(num, currentLanguage);
      }
      return String(value);
    };

    const fmtVal = (c?: MetricContribution) => fmtMetricNumber(c?.id, c?.rawValue);
    const fmtGoal = (c?: MetricContribution) => fmtMetricNumber(c?.id, c?.goal);

    const servicePts = (missed?.points || 0) + (response?.points || 0);
    const merchPts = (cantFind?.points || 0) + (ups?.points || 0);
    const experiencePts = exp?.points || 0;

    const service: TrioItem = {
      id: "Service",
      label: t("common.craveScoreCard.categories.service"),
      points: servicePts,
      details: t("common.craveScoreCard.categories.serviceDetails"),
      sub: [
        {
          label: t("common.craveScoreCard.metrics.missedPercent"),
          val: fmtVal(missed),
          goal: fmtGoal(missed),
        },
        {
          label: t("common.craveScoreCard.metrics.averageResponseSeconds"),
          val: fmtVal(response),
          goal: fmtGoal(response),
        },
      ],
    };

    const merchandise: TrioItem = {
      id: "Merchandise",
      label: t("common.craveScoreCard.categories.merchandise"),
      points: merchPts,
      details: t("common.craveScoreCard.categories.merchandiseDetails"),
      sub: [
        { label: t("common.craveScoreCard.metrics.ups"), val: fmtVal(ups), goal: fmtGoal(ups) },
        {
          label: t("common.craveScoreCard.metrics.cantFindsPercent"),
          val: fmtVal(cantFind),
          goal: fmtGoal(cantFind),
        },
      ],
    };

    const experienceItem: TrioItem = {
      id: "Experience",
      label: t("common.craveScoreCard.categories.experience"),
      points: experiencePts,
      details: t("common.craveScoreCard.categories.experienceDetails"),
      sub: [
        { label: t("common.craveScoreCard.metrics.rating"), val: fmtVal(exp), goal: fmtGoal(exp) },
      ],
    };

    return [service, merchandise, experienceItem];
  }, [contribById, t]);

  const trioMaxAbs = useMemo(() => {
    const vals = trio.map((t) => Math.abs(t.points));
    const m = Math.max(0, ...vals);
    return m > 0 ? m : 1;
  }, [trio]);

  // Insights generation
  type InsightSeverity = "critical" | "moderate" | "minor";
  type Insight = {
    id: string;
    category: TrioId;
    severity: InsightSeverity;
    message: string;
    badges: string[];
    estPointsGain: number; // estimated points gained if brought to goal
  };

  const insights = useMemo<Insight[]>(() => {
    const out: Insight[] = [];

    const pickSeverity = (gain: number): InsightSeverity => {
      const g = Math.abs(gain);
      if (g >= 2.5) return "critical";
      if (g >= 1.0) return "moderate";
      return "minor";
    };

    // Safe formatters for insights badges
    const fmtPctVal = (val: any) => {
      const num = typeof val === "number" ? val : Number(val);
      return Number.isFinite(num) ? formatPercentFromWhole(num, currentLanguage) : "—";
    };
    const fmtSecVal = (val: any) => {
      const num = typeof val === "number" ? val : Number(val);
      if (!Number.isFinite(num)) return "—";
      return formatDurationShort(num, currentLanguage);
    };
    const fmtNumVal = (val: any) => {
      const num = typeof val === "number" ? val : Number(val);
      return Number.isFinite(num) ? `${num}` : "—";
    };

    const get = (id: string) => contribById.get(id);

    // Service
    {
      const missed = get("missedPct");
      const response = get("avgResponseSec");
      const totalPts = (missed?.points || 0) + (response?.points || 0);
      const drivers: { key: string; label: string; c?: MetricContribution }[] = [
        {
          key: "missed",
          label: t("common.craveScoreCard.insights.labels.missedRequests"),
          c: missed,
        },
        {
          key: "response",
          label: t("common.craveScoreCard.insights.labels.responseTime"),
          c: response,
        },
      ];
      const bad = drivers.filter((d) => (d.c?.points || 0) < -0.5);
      if (totalPts < -0.25 && bad.length > 0) {
        const top = bad.sort((a, b) => (a.c?.points || 0) - (b.c?.points || 0))[0];
        const estGain = Math.min(0, totalPts) * -1; // bring to zero
        out.push({
          id: "ins-service",
          category: "Service",
          severity: pickSeverity(estGain),
          message: t("common.craveScoreCard.insights.messages.service", {
            topLabel: top.label,
            suffix: bad.length > 1 ? t("common.craveScoreCard.insights.labels.relatedDelays") : "",
          }),
          badges: [
            missed
              ? t("common.craveScoreCard.text.valueGoal", {
                  label: t("common.craveScoreCard.metrics.missed"),
                  value: fmtPctVal(missed.numericValue),
                  goalLabel: t("common.craveScoreCard.text.goal"),
                  goal: fmtPctVal(missed.goal),
                })
              : "",
            response
              ? t("common.craveScoreCard.text.valueGoal", {
                  label: t("common.craveScoreCard.metrics.averageResponseShort"),
                  value: fmtSecVal(response.numericValue),
                  goalLabel: t("common.craveScoreCard.text.goal"),
                  goal: fmtSecVal(response.goal),
                })
              : "",
            // `+${estGain.toFixed(1)} pts if fixed`,
          ].filter(Boolean),
          estPointsGain: estGain,
        });
      }
    }

    // Merchandise
    {
      const cantFind = get("cantFindPct");
      const ups = get("ups");
      const totalPts = (cantFind?.points || 0) + (ups?.points || 0);
      const badDrivers = [
        {
          key: "cantFind",
          label: t("common.craveScoreCard.insights.labels.cantFinds"),
          c: cantFind,
        },
        { key: "ups", label: t("common.craveScoreCard.insights.labels.unitsPerSession"), c: ups },
      ].filter((d) => (d.c?.points || 0) < -0.5);
      if (totalPts < -0.25 && badDrivers.length > 0) {
        const top = badDrivers.sort((a, b) => (a.c?.points || 0) - (b.c?.points || 0))[0];
        const estGain = Math.min(0, totalPts) * -1;
        out.push({
          id: "ins-merch",
          category: "Merchandise",
          severity: pickSeverity(estGain),
          message: t("common.craveScoreCard.insights.messages.merchandise", {
            topLabel: top.label,
          }),
          badges: [
            cantFind
              ? t("common.craveScoreCard.text.valueGoal", {
                  label: t("common.craveScoreCard.metrics.cantFinds"),
                  value: fmtPctVal(cantFind.numericValue),
                  goalLabel: t("common.craveScoreCard.text.goal"),
                  goal: fmtPctVal(cantFind.goal),
                })
              : "",
            ups
              ? t("common.craveScoreCard.text.valueGoal", {
                  label: t("common.craveScoreCard.metrics.ups"),
                  value: fmtNumVal(ups.numericValue),
                  goalLabel: t("common.craveScoreCard.text.goal"),
                  goal: fmtNumVal(ups.goal),
                })
              : "",
            // `+${estGain.toFixed(1)} pts if fixed`,
          ].filter(Boolean),
          estPointsGain: estGain,
        });
      }
    }

    // Experience
    {
      const exp = get("satisfaction");
      const totalPts = exp?.points || 0;
      if ((exp && exp.points < -0.5) || (exp && Number(exp.rawValue) < Number(exp.goal))) {
        const estGain = Math.min(0, totalPts) * -1;
        out.push({
          id: "ins-experience",
          category: "Experience",
          severity: pickSeverity(estGain),
          message: t("common.craveScoreCard.insights.messages.experience"),
          badges: [
            exp
              ? t("common.craveScoreCard.text.valueGoalPlus", {
                  label: t("common.craveScoreCard.metrics.rating"),
                  value: fmtNumVal(exp.numericValue),
                  goalLabel: t("common.craveScoreCard.text.goal"),
                  goal: fmtNumVal(exp.goal),
                })
              : "",
            // `+${estGain.toFixed(1)} pts if improved`,
          ].filter(Boolean),
          estPointsGain: estGain,
        });
      }
    }

    // Sort by severity then est gain and cap to 3
    const sevRank: Record<InsightSeverity, number> = { critical: 0, moderate: 1, minor: 2 };
    out.sort(
      (a, b) => sevRank[a.severity] - sevRank[b.severity] || b.estPointsGain - a.estPointsGain,
    );
    return out.slice(0, 3);
  }, [contribById, t, currentLanguage]);
  // Build a more prescriptive encouragement message using top insight when available
  type Encouragement = { icon: string; title: string; text: string };
  const encouragement: Encouragement = useMemo(() => {
    // When the Crave Score is zero, inform the user there is no data for this period
    if (score === 0) {
      return {
        icon: "—",
        title: t("common.craveScoreCard.encouragement.noActivity.title"),
        text: t("common.craveScoreCard.encouragement.noActivity.text"),
      };
    }
    const sevIcon = (sev: InsightSeverity): string =>
      sev === "critical" ? "✖" : sev === "moderate" ? "!" : "•";

    const num = (v: any): number | null => {
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const fmtPct = (v: any): string => {
      const n = num(v);
      return n == null ? "—" : formatPercentFromWhole(n, currentLanguage);
    };
    const fmtSec = (v: any): string => {
      const n = num(v);
      if (n == null) return "—";
      return formatDurationShort(n, currentLanguage);
    };

    const get = (id: string) => contribById.get(id);

    // Use the highest severity insight if present
    const top = insights.length > 0 ? insights[0] : null;
    if (!top) {
      return getGenericEncouragement(score, t);
    }

    if (top.category === "Service") {
      const missed = get("missedPct");
      const response = get("avgResponseSec");
      const missedBad = missed && (missed.points || 0) < -0.5;
      const respBad = response && (response.points || 0) < -0.5;
      if (missedBad && (!respBad || (missed?.points || 0) <= (response?.points || 0))) {
        const cur = fmtPct(missed?.numericValue);
        return {
          icon: sevIcon(top.severity),
          title: t("common.craveScoreCard.encouragement.service.fixMissedRequests.title"),
          text: t("common.craveScoreCard.encouragement.service.fixMissedRequests.text", { cur }),
        };
      }
      if (respBad) {
        const cur = fmtSec(response?.numericValue);
        const goal = fmtSec(response?.goal);
        return {
          icon: sevIcon(top.severity),
          title: t("common.craveScoreCard.encouragement.service.speedUpResponses.title"),
          text: t("common.craveScoreCard.encouragement.service.speedUpResponses.text", {
            goal,
            cur,
          }),
        };
      }
    }

    if (top.category === "Merchandise") {
      const cantFind = get("cantFindPct");
      const ups = get("ups");
      const cantFindBad = cantFind && (cantFind.points || 0) < -0.5;
      const upsBad = ups && (ups.points || 0) < -0.5;
      if (cantFindBad && (!upsBad || (cantFind?.points || 0) <= (ups?.points || 0))) {
        const cur = fmtPct(cantFind?.numericValue);
        const goal = fmtPct(cantFind?.goal);
        return {
          icon: sevIcon(top.severity),
          title: t("common.craveScoreCard.encouragement.merchandise.reduceCantFinds.title"),
          text: t("common.craveScoreCard.encouragement.merchandise.reduceCantFinds.text", {
            goal,
            cur,
          }),
        };
      }
      if (upsBad) {
        const cur = num(ups?.numericValue);
        const goal = num(ups?.goal);
        const curTxt = cur == null ? "—" : cur.toFixed(1);
        const goalTxt = goal == null ? "—" : goal.toFixed(1);
        return {
          icon: sevIcon(top.severity),
          title: t("common.craveScoreCard.encouragement.merchandise.liftUps.title"),
          text: t("common.craveScoreCard.encouragement.merchandise.liftUps.text", {
            goalTxt,
            curTxt,
          }),
        };
      }
    }

    if (top.category === "Experience") {
      const exp = get("satisfaction");
      const cur = num(exp?.numericValue);
      const goal = num(exp?.goal);
      const curTxt = cur == null ? "—" : cur.toFixed(1);
      const goalTxt = goal == null ? "—" : goal.toFixed(1);
      return {
        icon: sevIcon(top.severity),
        title: t("common.craveScoreCard.encouragement.experience.boostExperience.title"),
        text: t("common.craveScoreCard.encouragement.experience.boostExperience.text", {
          goalTxt,
          curTxt,
        }),
      };
    }

    // Fallback
    return getGenericEncouragement(score, t);
  }, [score, insights, contribById, t, currentLanguage]);

  // Small diverging bar component centered at 0
  const ImpactBar: React.FC<{ points: number; maxAbs: number; barHeightPx?: number }> = ({
    points,
    maxAbs,
    barHeightPx = 8,
  }) => {
    const pct = Math.min(1, Math.abs(points) / (maxAbs || 1));
    const widthPct = `${(pct * 50).toFixed(2)}%`; // extent from center to a side (max 50%)
    const isPositive = points > 0;
    const isNegative = points < 0;
    const label = `${points > 0 ? "+" : points < 0 ? "−" : ""}${Math.abs(points).toFixed(1)} pts`;

    const zeroDot = Math.max(2, Math.round(barHeightPx * 0.6));

    return (
      <div
        className="relative w-full"
        style={{ height: barHeightPx }}
        role="img"
        aria-label={t("common.craveScoreCard.aria.impactRelativeToAverage", { label })}
        title={t("common.craveScoreCard.text.impactVsAverage", { label })}
      >
        {/* track */}
        <div className="absolute inset-0 rounded-full bg-slate-100" />
        {/* center tick: ensure perfectly vertical centered regardless of container quirks */}
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-px bg-slate-300"
          style={{ height: barHeightPx }}
        />
        {/* positive fill (to the right) */}
        {isPositive && (
          <div
            className="absolute top-0 bottom-0 rounded-r-full rtl:rounded-r-none rtl:rounded-l-full"
            style={{
              insetInlineStart: "50%",
              width: widthPct,
              transition:
                !mounted || prefersReducedMotion
                  ? "none"
                  : "width 2000ms cubic-bezier(.22,1,.36,1)",
              background: isRtl
                ? `linear-gradient(270deg, ${BLUE_GRADIENT_FROM} 0%, ${BLUE_GRADIENT_TO} 100%)`
                : `linear-gradient(90deg, ${BLUE_GRADIENT_FROM} 0%, ${BLUE_GRADIENT_TO} 100%)`,
            }}
          />
        )}
        {/* negative fill (to the left) */}
        {isNegative && (
          <div
            className="absolute top-0 bottom-0 rounded-l-full rtl:rounded-l-none rtl:rounded-r-full"
            style={{
              insetInlineEnd: "50%",
              width: widthPct,
              transition:
                !mounted || prefersReducedMotion
                  ? "none"
                  : "width 1000ms cubic-bezier(.22,1,.36,1)",
              background: isRtl
                ? `linear-gradient(to right, ${NEG_GRADIENT_FROM} 0%, ${NEG_GRADIENT_TO} 100%)`
                : `linear-gradient(to left, ${NEG_GRADIENT_FROM} 0%, ${NEG_GRADIENT_TO} 100%)`,
            }}
          />
        )}
        {/* zero indicator */}
        {!isPositive && !isNegative && (
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-400"
            style={{ width: zeroDot, height: zeroDot }}
          />
        )}
      </div>
    );
  };
  const pillBg = "bg-blue-100 text-blue-800";

  // Bold star-delimited emphasis segments authored directly in translation copy.
  const highlightMetrics = (text: string): React.ReactNode => {
    if (!text) return text;
    return text
      .split(/(\*[^*]+\*)/g)
      .filter(Boolean)
      .map((part, index) => {
        if (part.startsWith("*") && part.endsWith("*")) {
          return <strong key={`hm-${index}`}>{part.slice(1, -1)}</strong>;
        }

        return part;
      });
  };

  return (
    <Box
      className="crave-score-card-root relative rounded-2xl w-full box-border bg-white border border-slate-100 shadow-md shadow-slate-100 flex flex-col"
      sx={{ p: { xs: 3, lg: 4 } }}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="crave-score-title text-[20px] font-semibold bg-gradient-to-r from-[#0066ff] to-[#00B7FF] bg-clip-text text-transparent">
            {t("common.craveScoreCard.text.title")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isPending && (
            <div className="flex items-center gap-1" aria-live="polite">
              <svg
                className="animate-spin h-3 w-3 text-slate-500"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              <span className="sr-only">{t("common.craveScoreCard.text.updatingScore")}</span>
            </div>
          )}
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${pillBg}`}>
            {status.label}
          </span>
        </div>
      </div>

      <div className="mt-0 text-sm text-slate-600">
        {t("common.craveScoreCard.text.scoreDescription")}
      </div>

      <Box
        className="crave-score-main-layout mt-6"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "column", lg: "row" },
          alignItems: { xs: "flex-start", md: "stretch" },
          gap: { xs: 2.75, md: 3, lg: 2 },
        }}
      >
        <Box
          className="crave-score-primary-panels w-full"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "stretch" },
            gap: { xs: 2.75, md: 1.5, lg: 2 },
            flex: { lg: "0 0 auto" },
            width: { xs: "100%", lg: "auto" },
          }}
        >
          {/* Left: Gauge */}
          <Box
            className="crave-score-gauge-panel w-full"
            sx={{ flex: { md: "0 0 auto" }, width: { md: 220 } }}
          >
            <div className="flex justify-center">
              <div className="relative" style={{ width: size, height: size }}>
                <svg
                  width={size}
                  height={size}
                  viewBox={`0 0 ${size} ${size}`}
                  role="img"
                  aria-label={t("common.craveScoreCard.aria.scoreOutOfHundred", {
                    score,
                    statusLabel: status.label,
                  })}
                >
                  <title>
                    {t("common.craveScoreCard.text.scoreOutOfHundred", {
                      score,
                      statusLabel: status.label,
                    })}
                  </title>
                  <defs>
                    {/* Blue gradient for good/ok status */}
                    <linearGradient id={`${gradId}-blue`} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={BLUE_GRADIENT_FROM} />
                      <stop offset="100%" stopColor={BLUE_GRADIENT_TO} />
                    </linearGradient>
                    {/* Red/orange gradient for warn/needs attention status (matches ImpactBar negative) */}
                    <linearGradient id={`${gradId}-red`} x1="100%" y1="0%" x2="0%" y2="0%">
                      <stop offset="0%" stopColor={NEG_GRADIENT_FROM} />
                      <stop offset="100%" stopColor={NEG_GRADIENT_TO} />
                    </linearGradient>
                  </defs>
                  {/* Background track */}
                  <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke="#eef2ff"
                    strokeWidth={stroke}
                  />
                  {/* Progress arc: render both blue and red, crossfade based on status */}
                  <g style={{ transform: `rotate(-90deg)`, transformOrigin: "50% 50%" }}>
                    {/* Blue (OK/Success) */}
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      fill="none"
                      stroke={`url(#${gradId}-blue)`}
                      strokeWidth={stroke}
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={dashOffset}
                      style={{
                        transition:
                          !mounted || prefersReducedMotion
                            ? "none"
                            : "stroke-dashoffset 600ms cubic-bezier(.22,1,.36,1), opacity 400ms ease",
                        opacity: isWarnVisual ? 0 : 1,
                      }}
                      filter="url(#glow)"
                    />
                    {/* Red (Needs attention) */}
                    <circle
                      cx={size / 2}
                      cy={size / 2}
                      r={radius}
                      fill="none"
                      stroke={`url(#${gradId}-red)`}
                      strokeWidth={stroke}
                      strokeLinecap="round"
                      strokeDasharray={circumference}
                      strokeDashoffset={dashOffset}
                      style={{
                        transition:
                          !mounted || prefersReducedMotion
                            ? "none"
                            : "stroke-dashoffset 600ms cubic-bezier(.22,1,.36,1), opacity 400ms ease",
                        opacity: isWarnVisual ? 1 : 0,
                      }}
                      filter="url(#glow)"
                    />
                  </g>
                </svg>
                {/* Center content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center select-none">
                  <div
                    className={`crave-score-value ${isWarnVisual ? "crave-score-value--warn bg-gradient-to-r from-[#FF9F0A] to-[#FF3B30] bg-clip-text text-transparent" : "crave-score-value--ok"} text-4xl font-extrabold tracking-tight`}
                    style={isWarnVisual ? undefined : { color }}
                  >
                    {scoreText}
                  </div>
                  <div className="text-xs text-slate-500">
                    {t("common.craveScoreCard.text.outOfHundred")}
                  </div>
                </div>
              </div>
            </div>
          </Box>

          {/* Middle: Encouragement */}
          <Box
            className="crave-score-encouragement-panel w-full"
            sx={{
              flex: { md: "1 1 0", lg: "0 0 auto" },
              width: { xs: "100%", md: "auto", lg: 220 },
              minWidth: 0,
            }}
          >
            <Box
              className={`crave-score-encouragement-card ${isWarnVisual ? "crave-score-encouragement-card--warn" : "crave-score-encouragement-card--ok"} rounded-2xl shadow-md border border-white/10 overflow-hidden`}
              sx={{
                background: isWarnVisual
                  ? isRtl
                    ? `/* @noflip */ linear-gradient(225deg, ${NEG_GRADIENT_FROM} 0%, ${NEG_GRADIENT_TO} 100%)`
                    : `/* @noflip */ linear-gradient(135deg, ${NEG_GRADIENT_FROM} 0%, ${NEG_GRADIENT_TO} 100%)`
                  : isRtl
                    ? `/* @noflip */ linear-gradient(225deg, ${BLUE_GRADIENT_FROM} 0%, ${BLUE_GRADIENT_TO} 100%)`
                    : `/* @noflip */ linear-gradient(135deg, ${BLUE_GRADIENT_FROM} 0%, ${BLUE_GRADIENT_TO} 100%)`,
                minHeight: { xs: "auto", md: size },
                display: "grid",
                placeItems: "center",
              }}
            >
              <Box
                className="w-full text-center text-white select-none"
                sx={{
                  px: { xs: 1.25, lg: 1.5 },
                  py: { xs: 2, md: 1.25, lg: 1.5 },
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    justifyItems: "center",
                    gap: { xs: 0.75, md: 1 },
                    width: "100%",
                  }}
                >
                  <div
                    className={`crave-score-encouragement-icon ${isWarnVisual ? "crave-score-encouragement-icon--warn" : "crave-score-encouragement-icon--ok"} text-5xl font-bold leading-none`}
                    aria-hidden="true"
                  >
                    {encouragement.icon}
                  </div>
                  <Box
                    className="crave-score-encouragement-title font-semibold"
                    sx={{ fontSize: { xs: "1rem", sm: "1.125rem" }, lineHeight: 1.2 }}
                  >
                    {highlightMetrics(encouragement.title)}
                  </Box>
                  <Box
                    className="crave-score-encouragement-text opacity-90"
                    sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" }, lineHeight: 1.35 }}
                  >
                    {highlightMetrics(encouragement.text)}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right: What's Driving This */}
        <Box
          className="w-full crave-score-driving-section"
          sx={{ flex: { lg: 1 }, width: { xs: "100%", lg: "auto" }, minWidth: 0 }}
        >
          <div className="font-semibold text-sm mb-4">
            {t("common.craveScoreCard.text.whatsDrivingThis")}
          </div>
          <ul
            id="whats-driving"
            className="text-xs divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden bg-white"
          >
            {/* Header labels above the bar column: Worse | Average | Better */}
            <li className="px-4 pt-3 pb-2 bg-white" aria-hidden>
              <div className="flex items-center justify-between gap-3">
                {/* left spacer to mirror label column */}
                <div className="text-sm font-semibold text-slate-900 invisible">
                  {t("common.craveScoreCard.text.label")}
                </div>
                {/* right: same fixed width container as bars, with 3-way labels */}
                <Box
                  className="shrink-0"
                  sx={{ width: { xs: 224, sm: 256, md: 288, lg: 320, xl: 384 } }}
                >
                  <div className="grid grid-cols-3 text-[11px] text-slate-500">
                    <div className="text-left rtl:text-right">
                      {t("common.craveScoreCard.text.worse")}
                    </div>
                    <div className="text-center">{t("common.craveScoreCard.text.average")}</div>
                    <div className="text-right rtl:text-left">
                      {t("common.craveScoreCard.text.better")}
                    </div>
                  </div>
                </Box>
              </div>
            </li>
            {trio.map((trioItem) => {
              const effect = formatPoints(trioItem.points);
              const effectLabel = t("common.craveScoreCard.text.points", { points: effect });
              const label = t("common.craveScoreCard.aria.categoryImpact", {
                category: trioItem.label,
                points: effectLabel,
              });
              return (
                <li key={trioItem.id} className="px-4 py-3 bg-white">
                  <button
                    type="button"
                    onClick={() => toggleOpen(trioItem.id)}
                    aria-expanded={isOpen(trioItem.id)}
                    aria-controls={`gd-${trioItem.id}`}
                    className="w-full focus:outline-none focus:ring-2 focus:ring-blue-300 rounded"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-slate-900">{trioItem.label}</div>
                      <Box
                        className="shrink-0 flex items-center"
                        sx={{ width: { xs: 160, sm: 224, md: 256, lg: 288, xl: 384 } }}
                        role="img"
                        aria-label={label}
                        title={label}
                      >
                        <div className="flex-1">
                          <ImpactBar
                            points={trioItem.points}
                            maxAbs={trioMaxAbs}
                            barHeightPx={16}
                          />
                        </div>
                      </Box>
                    </div>
                  </button>

                  {/* Inline granular details for this category — CSS-only smooth expand/collapse */}
                  {(() => {
                    const metricIds =
                      trioItem.id === "Service"
                        ? ["missedPct", "avgResponseSec"]
                        : trioItem.id === "Merchandise"
                          ? ["ups", "cantFindPct"]
                          : ["satisfaction"];
                    return (
                      <div
                        id={`gd-${trioItem.id}`}
                        className={`mt-2 grid ${isOpen(trioItem.id) ? "[grid-template-rows:1fr] opacity-100" : "[grid-template-rows:0fr] opacity-0"} transition-[grid-template-rows,opacity] duration-800 ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-none motion-reduce:duration-0`}
                        aria-hidden={!isOpen(trioItem.id)}
                      >
                        <div className="overflow-hidden">
                          <ul className="space-y-2" aria-live="polite">
                            {metricIds.map((mid) => {
                              const c = contribById.get(mid);
                              if (!c) return null;
                              const valTxt = formatMetricValue(
                                mid,
                                (c as any).numericValue ?? (c as any).rawValue,
                              );
                              const goalTxt = formatMetricValue(mid, (c as any).goal);
                              return (
                                <li
                                  key={mid}
                                  className="flex items-center justify-between gap-3 py-2"
                                >
                                  <div className="min-w-0">
                                    <div className="truncate font-medium text-slate-800">
                                      {c.label}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                      {t("common.craveScoreCard.text.value")}{" "}
                                      <span className="font-mono">{valTxt}</span>
                                      {mid === "satisfaction" && (
                                        <span
                                          className="ml-0 text-[10px] text-black align-middle"
                                          aria-hidden="true"
                                        >
                                          ★
                                        </span>
                                      )}{" "}
                                      • {t("common.craveScoreCard.text.goal")}{" "}
                                      <span className="font-mono">{goalTxt}</span>
                                      {mid === "satisfaction" && (
                                        <span
                                          className="ml-0 text-[10px] text-black align-middle"
                                          aria-hidden="true"
                                        >
                                          ★
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <Box
                                    className="shrink-0 flex items-center"
                                    sx={{
                                      width: { xs: 160, sm: 224, md: 256, lg: 288, xl: 384 },
                                      pl: { xs: 1.5, md: 2 },
                                      pr: { xs: 0, md: 0 },
                                    }}
                                  >
                                    <div className="flex-1">
                                      <ImpactBar
                                        points={c.points}
                                        maxAbs={maxAbs}
                                        barHeightPx={4}
                                      />
                                    </div>
                                  </Box>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    );
                  })()}
                </li>
              );
            })}
          </ul>
          {/* Granular contributions toggle (moved below "What's Driving This") */}
          <div className="mt-3 flex items-center justify-end">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded px-3 py-1.5"
              onClick={() =>
                setAll(!(expanded.Service && expanded.Merchandise && expanded.Experience))
              }
              aria-expanded={expanded.Service && expanded.Merchandise && expanded.Experience}
              aria-controls="whats-driving"
            >
              <span aria-hidden="true">
                {expanded.Service || expanded.Merchandise || expanded.Experience
                  ? "▾"
                  : theme.direction == "rtl"
                    ? "◂"
                    : "▸"}
              </span>
              {expanded.Service || expanded.Merchandise || expanded.Experience
                ? t("common.craveScoreCard.actions.collapseAllDetails")
                : t("common.craveScoreCard.actions.expandAllDetails")}
            </button>
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default CraveScoreCard;
